/**
 * CardTypesPage — Products & Features · Card Types.
 *
 * Reads /v3/config/card-types. PCB has 4 active card designs as of
 * 2026-05-08 (Collette confirmed).
 *
 * Phase B (2026-06-14): create / edit / soft-delete wired through the typed
 * write surface `client.retail.admin.products.cardTypes` (POST/DELETE
 * /v3/admin/card-types). Bearer-only — bypassKey arg is '' (no bypass
 * prompt). Structure mirrors Identity/RolesPage.tsx.
 *
 * Note: the read row (CardType) and the write body (CardTypeUpsertBody) carry
 * different field sets. The read projection exposes only code + name (mapped
 * to description on edit); the write-only fields (binPrefix, isoType,
 * cardCategory, prodCode, issuerBankCode) are entered/confirmed in the form.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CardType } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import type { CardTypeUpsertBody } from '@retail-ops/api-client';

interface EditDraft {
  code: string;
  description: string;
  binPrefix: string;
  prodCode: string;
  isoType: string;
  cardCategory: string;
  issuerBankCode: string;
  isActive: boolean;
  sortOrder: string;
  /** Newly-uploaded artwork (base64, no data: prefix). undefined = leave the
   *  existing image untouched on save (BACKLOG-0873). */
  imageBase64?: string;
  imageContentType?: string;
  /** Preview URL for the current/uploaded image (data: URI or the API path). */
  imagePreview?: string;
  /** True for a brand-new row (code editable + create-mode title). */
  isNew: boolean;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/** Resolve a config row's design thumbnail URL, if it has uploaded artwork. */
function rowImageUrl(c: CardType): string | undefined {
  const anyC = c as unknown as Record<string, unknown>;
  const imagePath = typeof anyC['imagePath'] === 'string' ? (anyC['imagePath'] as string) : undefined;
  return imagePath ? `${API_BASE}${imagePath}` : undefined;
}

/** Read an uploaded image File into { base64, contentType } (strips data: prefix). */
async function fileToBase64(file: File): Promise<{ base64: string; contentType: string }> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error ?? new Error('read failed'));
    fr.readAsDataURL(file);
  });
  const comma = dataUrl.indexOf(',');
  return { base64: dataUrl.slice(comma + 1), contentType: file.type || 'image/png' };
}

const CARD_CATEGORY_OPTIONS = [
  { value: 'Personal', label: 'Personal' },
  { value: 'Business', label: 'Business' },
  { value: 'ATM', label: 'ATM' },
];

function newDraft(): EditDraft {
  return {
    code: '',
    description: '',
    binPrefix: '',
    prodCode: '',
    isoType: '',
    cardCategory: 'Personal',
    issuerBankCode: '',
    isActive: true,
    sortOrder: '',
    isNew: true,
  };
}

/**
 * Map an existing read row into a form draft. The config projection only
 * carries code + name, so the write-only fields start blank for the admin to
 * confirm/fill on edit.
 */
function rowToDraft(c: CardType): EditDraft {
  const anyC = c as unknown as Record<string, unknown>;
  const sortOrder = typeof anyC['sortOrder'] === 'number' ? String(anyC['sortOrder']) : '';
  return {
    code: c.code,
    description: c.name ?? '',
    binPrefix: '',
    prodCode: '',
    isoType: '',
    cardCategory: 'Personal',
    issuerBankCode: '',
    isActive: c.isActive ?? true,
    sortOrder,
    // imageBase64 left undefined → editing metadata keeps the existing image.
    imagePreview: rowImageUrl(c),
    isNew: false,
  };
}

export function CardTypesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<CardType> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await client.retail.config.cardTypes();
      if (resp.success && Array.isArray(resp.data)) {
        setRows(resp.data);
      } else {
        setError(resp.error ?? 'Failed to load card types.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const remove = useCallback(
    async (code: string): Promise<void> => {
      if (!window.confirm(`Soft-delete card type "${code}"? It will be marked inactive.`)) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.products.cardTypes.remove('', code);
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<CardType>> = useMemo(() => [
    {
      header: 'Design',
      cell: (c) => {
        const url = rowImageUrl(c);
        return url ? (
          <img
            src={url}
            alt={`${c.name ?? c.code} design`}
            style={{ width: 56, height: 35, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--ink-100, #e2e8f0)' }}
          />
        ) : (
          <span className="cell-dim">—</span>
        );
      },
    },
    {
      header: 'Code',
      cell: (c) => <code className="cell-mono--bold">{c.code}</code>,
    },
    { header: 'Name', cell: (c) => c.name },
    {
      header: 'Network',
      cell: (c) => (c.cardNetwork ? <Pill tone="muted">{c.cardNetwork}</Pill> : <span className="cell-dim">—</span>),
    },
    {
      header: 'ATM Limit',
      cell: (c) =>
        c.dailyAtmLimit ? (
          <span className="cell-mono">${c.dailyAtmLimit}</span>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: 'Purchase Limit',
      cell: (c) =>
        c.dailyPurchaseLimit ? (
          <span className="cell-mono">${c.dailyPurchaseLimit}</span>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: 'Status',
      cell: (c) => (c.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
    },
    {
      header: '',
      cell: (c) => (
        <>
          <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(c))}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void remove(c.code)}>
            Delete
          </Button>
        </>
      ),
    },
  ], [remove]);

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      setSaving(true);
      setSaveErr(null);
      try {
        const sortOrderNum = d.sortOrder.trim() === '' ? null : Number(d.sortOrder.trim());
        const body: CardTypeUpsertBody = {
          code: d.code.trim(),
          description: d.description.trim(),
          binPrefix: d.binPrefix.trim(),
          prodCode: d.prodCode.trim() || undefined,
          isoType: d.isoType.trim(),
          cardCategory: d.cardCategory,
          issuerBankCode: d.issuerBankCode.trim() || null,
          isActive: d.isActive,
          sortOrder: Number.isFinite(sortOrderNum as number) ? sortOrderNum : null,
          // Only send the image when the admin uploaded a new one (BACKLOG-0873);
          // otherwise leave the stored artwork untouched.
          ...(d.imageBase64 !== undefined ? { imageBase64: d.imageBase64, imageContentType: d.imageContentType } : {}),
        };
        const resp = await client.retail.admin.products.cardTypes.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) return <Banner tone="err">Could not load card types: {error}</Banner>;
  if (!rows) return <Spinner label="Loading card types…" />;

  return (
    <>
      <Card
        title={`Card Types — ${rows.length}`}
        actions={<Button onClick={() => setDraft(newDraft())}>New</Button>}
      >
        <DataTable<CardType>
          columns={columns}
          rows={rows}
          rowKey={(c) => c.code}
          emptyState={<p className="empty">No card types defined.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft ? (draft.isNew ? 'New card type' : `Edit card type — ${draft.code}`) : ''}
        onClose={() => (saving ? undefined : setDraft(null))}
        width={580}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <TextField
              label="Code"
              value={draft.code}
              onChange={(v) => setDraft({ ...draft, code: v })}
              disabled={!draft.isNew}
              monospace
              required
              hint={draft.isNew ? 'Card type code (read-only after create).' : 'Read-only after create.'}
            />
            <TextField
              label="Description"
              value={draft.description}
              onChange={(v) => setDraft({ ...draft, description: v })}
              required
              hint="Customer-facing design name (e.g. Rt 66, Dog)."
            />

            {/* Card-design artwork (BACKLOG-0873) */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                Card Design Image
              </label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {draft.imagePreview ? (
                  <img
                    src={draft.imagePreview}
                    alt="card design preview"
                    style={{ width: 96, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--ink-100, #e2e8f0)' }}
                  />
                ) : (
                  <div style={{ width: 96, height: 60, borderRadius: 6, border: '1px dashed var(--ink-100, #cbd5e1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--ink-600, #64748b)' }}>
                    No image
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      void fileToBase64(f).then(({ base64, contentType }) => {
                        setDraft((prev) => (prev ? { ...prev, imageBase64: base64, imageContentType: contentType, imagePreview: `data:${contentType};base64,${base64}` } : prev));
                      });
                    }}
                  />
                  {draft.imagePreview && (
                    <button
                      type="button"
                      style={{ fontSize: 12, color: 'var(--pcb-red, #b10505)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                      onClick={() => setDraft((prev) => (prev ? { ...prev, imageBase64: '', imageContentType: undefined, imagePreview: undefined } : prev))}
                    >
                      Remove image
                    </button>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--ink-600, #64748b)' }}>PNG/JPG/WebP. Shown to the FSR + customer.</span>
                </div>
              </div>
            </div>

            <TextField
              label="BIN Prefix"
              value={draft.binPrefix}
              onChange={(v) => setDraft({ ...draft, binPrefix: v })}
              monospace
              hint="Core Director BIN (card programs only — leave blank for a design)."
            />
            <TextField
              label="Product Code"
              value={draft.prodCode}
              onChange={(v) => setDraft({ ...draft, prodCode: v })}
            />
            <TextField
              label="ISO Type"
              value={draft.isoType}
              onChange={(v) => setDraft({ ...draft, isoType: v })}
            />
            <SelectField
              label="Card Category"
              value={draft.cardCategory}
              onChange={(v) => setDraft({ ...draft, cardCategory: v })}
              options={CARD_CATEGORY_OPTIONS}
              required
            />
            <TextField
              label="Issuer Bank Code"
              value={draft.issuerBankCode}
              onChange={(v) => setDraft({ ...draft, issuerBankCode: v })}
            />
            <TextField
              label="Sort Order"
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v.replace(/[^0-9-]/g, '') })}
              monospace
              hint="Lower = shown first in the design picker."
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
